import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useForm } from '@/components/react-hook-form';
import { invalidateCacheField } from '@/graphql/client.utils';
// The mutation is kind-agnostic, so both link panes reuse the legacy trowser's;
// it moves here when the trowser is deleted at flag graduation.
import { useUpdateDirectHoldingsEntitiesMutation } from '@/modules/household/LinkHouseholdDataTrowser/DirectHoldingsStep/graphql/UpdateDirectHoldingsEntities.generated';

import { PaneForm } from '../../../ManageIntegrationDataModal.types';
import {
  buildEntityLinkFormValues,
  EntityLinkFormShape,
  EntityLinkRow,
} from '../components/EntityLinkTable.types';
import { generateEntityUpdates } from '../linkData.utils';

export interface EntityLinkForm extends PaneForm {
  formMethods: UseFormReturn<EntityLinkFormShape>;
  seed: (rows: EntityLinkRow[]) => void;
  /**
   * Integration entities this pane has attached to an existing Luminary
   * entity, committed or pending, so the import panes can subtract them.
   */
  getClaimedIntegrationEntityIds: () => string[];
}

/**
 * Form and save shared by the two "Link data" panes. Both normalize their very
 * different queries down to `EntityLinkRow`, so the diff and the mutation are
 * the same on either side.
 *
 * @param contentDescription shown in the error toast, e.g. "direct holdings".
 */
export function useEntityLinkForm(
  contentDescription: string,
  onSaved?: () => void
): EntityLinkForm {
  const apolloClient = useApolloClient();
  const { createErrorFeedback } = useFeedback();

  const formMethods = useForm<EntityLinkFormShape>({
    defaultValues: { entities: {} },
  });
  const {
    getValues,
    reset,
    handleSubmit,
    formState: { isDirty, defaultValues },
  } = formMethods;

  const seed = useCallback(
    (rows: EntityLinkRow[]) => {
      if (isDirty) return;
      reset(buildEntityLinkFormValues(rows));
    },
    [isDirty, reset]
  );

  // No `onError`: Apollo only rejects the promise when the caller hasn't
  // handled the error, and the commit needs that rejection to stop.
  const [updateEntities] = useUpdateDirectHoldingsEntitiesMutation();

  const save = handleSubmit(async ({ entities }) => {
    const updateEntitiesInput = generateEntityUpdates(
      entities,
      defaultValues?.entities
    );
    if (!updateEntitiesInput.length) return;

    try {
      await updateEntities({
        variables: { updateEntities: updateEntitiesInput },
      });
    } catch (error) {
      createErrorFeedback(
        `Failed to link ${contentDescription}. Please try again.`
      );
      throw error;
    }

    onSaved?.();
    invalidateCacheField('entities', apolloClient);
    reset({ entities });
  });

  const getClaimedIntegrationEntityIds = useCallback(
    () =>
      Object.values(getValues().entities).flatMap(
        (entity) => entity.integrationEntities
      ),
    [getValues]
  );

  return { formMethods, save, seed, getClaimedIntegrationEntityIds };
}
